import React from "react";
import api, { formatTL, formatDuration, formatDateTime } from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plate } from "@/components/Plate";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ImageSquare, TicketIcon, Sparkle, Info,
} from "@phosphor-icons/react";
import { subscribeToPush } from "@/lib/push";
import { useNavigate } from "react-router-dom";

function timeAgo(iso) {
  if (!iso) return "";
  const diffS = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffS < 60) return `${diffS}sn önce`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}dk önce`;
  return `${Math.floor(diffS / 3600)}sa önce`;
}

export default function CustomerHome() {
  const { user } = useAuth();
  const nav = useNavigate();

  // SEC-001: Plaka doğrulama durumunu takip et → doğrulanmamış plaka için
  // "Talep Formu" göster; onay gelene kadar oturum listesi boş kalır.
  const { data: plateStatus } = useLiveQuery(
    "plate_status",
    async () => (await api.get("/customer/plate-status")).data,
    { intervalMs: 10000 },
  );
  const [plateInput, setPlateInput] = React.useState("");
  const requestPlate = async () => {
    const p = (plateInput || "").toUpperCase().replace(/\s/g, "");
    if (p.length < 4) { toast.error("Geçersiz plaka"); return; }
    try {
      const { data } = await api.post("/customer/request-plate", { plate: p });
      toast.success(data.message || "Talebiniz alındı");
      setPlateInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Talep gönderilemedi");
    }
  };

  const { data: sessions } = useLiveQuery(
    TAGS.SESSIONS,
    async () => (await api.get("/customer/sessions")).data,
    { intervalMs: 8000 },
  );
  const { data: justExited } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.UNPAID],
    async () => (await api.get("/customer/just-exited?minutes=60")).data,
    { intervalMs: 3000 },
  );

  const list = sessions || [];
  const active = list.filter((s) => s.status === "active");
  const activeCurrent = active[0];
  const exitedList = justExited || [];
  const otherUnpaid = list.filter(
    (s) => s.status === "closed"
        && s.payment_status === "bekliyor"
        && !exitedList.some((e) => e.id === s.id),
  );

  const enablePush = async () => {
    try { await subscribeToPush(); toast.success("Bildirimler açıldı"); }
    catch (e) { toast.error(e?.message || "Bildirimler açılamadı"); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Hoş geldin</div>
        <h1 className="text-3xl font-bold tracking-tight">{user?.name}</h1>
      </div>

      {/* SEC-001: Plaka doğrulanmamışsa talep formu göster */}
      {plateStatus && !plateStatus.plate_verified && (
        <Card className="rounded-sm border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Info size={22} weight="fill" className="text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900">
                  {plateStatus.has_pending_request ? "Plakanız Onay Bekliyor" : "Plakanızı Bildirin"}
                </div>
                <div className="text-xs text-amber-800/80 mt-1">
                  {plateStatus.has_pending_request
                    ? `${plateStatus.pending_plate} — Otopark yönetimi kısa süre içinde onaylayacak. Onay sonrası araç geçmişiniz burada görünecek.`
                    : "Araç geçmişinizi görebilmek için önce plakanızın hesabınıza bağlanması gerekiyor. Otopark yönetimi manuel olarak onaylayacak."}
                </div>
              </div>
            </div>
            {!plateStatus.has_pending_request && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={plateInput}
                  onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                  placeholder="34ABC123"
                  className="flex-1 px-3 py-2 border border-amber-300 rounded-sm font-plate uppercase text-sm bg-white"
                  data-testid="customer-plate-input"
                  maxLength={12}
                />
                <Button
                  onClick={requestPlate}
                  data-testid="customer-request-plate-btn"
                  className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white uppercase tracking-widest text-xs font-semibold"
                >
                  Talep Et
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Active subscription badge */}
      {user?.subscription_expires && (
        <Card className="rounded-sm border-emerald-300 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkle size={22} weight="fill" className="text-emerald-600" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
                Aboneliğiniz Aktif
              </div>
              <div className="text-sm font-mono">
                Bitiş: {formatDateTime(user.subscription_expires)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Just Exited hero panel */}
      {exitedList.length > 0 && (
        <Card className="rounded-sm border-2 border-primary bg-primary/5" data-testid="cust-just-exited-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="pulse-dot bg-primary" />
              <h3 className="font-bold uppercase tracking-widest text-sm">
                Az Önce Çıktı — Operatöre Ödeyin
              </h3>
            </div>
            {exitedList.map((s) => (
              <ExitedInfoCard key={s.id} s={s} onGoToCoupons={() => nav("/customer/coupons")} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Currently parked */}
      {activeCurrent ? (
        <Card className="rounded-sm border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Şu an park halinde</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="pulse-dot" />
                  <span className="text-xs">
                    {activeCurrent.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
                  </span>
                </div>
              </div>
              <Plate country={activeCurrent.country_code}>{activeCurrent.plate}</Plate>
            </div>
            <div className="grid grid-cols-2 gap-4 py-4 border-y border-border">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Süre</div>
                <div className="text-2xl font-mono font-bold mt-1">
                  {formatDuration(activeCurrent.current_duration_minutes)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {activeCurrent.subscriber_free ? "Ücret" : "Anlık Ücret"}
                </div>
                {activeCurrent.subscriber_free ? (
                  <div className="text-2xl font-mono font-bold text-emerald-600 mt-1" data-testid="cust-running-fee">
                    MUAF
                  </div>
                ) : (
                  <div className="text-2xl font-mono font-bold text-primary mt-1" data-testid="cust-running-fee">
                    {formatTL(activeCurrent.running_fee)}
                  </div>
                )}
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-3">
              {activeCurrent.subscriber_free
                ? "Aktif aboneliğiniz sayesinde bu oturum ücretsiz."
                : "Çıkışta ücret otomatik hesaplanır. Ödeme için operatöre başvurun."}
            </div>
          </CardContent>
        </Card>
      ) : exitedList.length === 0 && (
        <Card className="rounded-sm border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Şu an aktif oturumun yok.<br />Bir plaka okutulduğunda burada görüneceksin.
          </CardContent>
        </Card>
      )}

      {/* Older unpaid */}
      {otherUnpaid.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest mb-3">Diğer Ödenmemiş</h3>
          <div className="space-y-2">
            {otherUnpaid.map((s) => (
              <Card key={s.id} className="rounded-sm border-border">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <Plate country={s.country_code}>{s.plate}</Plate>
                    <div className="text-xs text-muted-foreground mt-1">{formatDateTime(s.exit_time)}</div>
                  </div>
                  <div className="font-mono font-bold text-destructive text-lg">{formatTL(s.fee)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Push CTA */}
      {"Notification" in window && (
        <Card className="rounded-sm border-border border-dashed">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Bildirimleri aç</div>
              <div className="text-xs text-muted-foreground">Çıkış ve ödeme uyarıları için</div>
            </div>
            <Button data-testid="cust-enable-push" onClick={enablePush}
                    variant="outline" size="sm" className="rounded-sm">Aç</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ExitedInfoCard({ s, onGoToCoupons }) {
  return (
    <div className="bg-white rounded-sm border border-primary/30 p-3 space-y-3" data-testid={`cust-exit-${s.plate}`}>
      <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
        <div className="flex gap-1 items-center">
          <PhotoTile src={s.entry_image_url} label="GİRİŞ" />
          <ArrowRight size={12} className="text-muted-foreground" />
          <PhotoTile src={s.exit_image_url} label="ÇIKIŞ" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Plate country={s.country_code}>{s.plate}</Plate>
            <span className="text-[10px] text-muted-foreground uppercase">{timeAgo(s.exit_time)}</span>
          </div>
          <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
            Süre: {formatDuration(s.duration_minutes)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ödenecek</div>
          <div className="text-2xl font-bold font-mono text-primary leading-none" data-testid={`cust-exit-fee-${s.plate}`}>
            {formatTL(s.fee)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-sm text-xs">
        <Info size={14} className="text-muted-foreground shrink-0" />
        <span className="text-muted-foreground">
          Otoparktan çıkışta operatöre nakit veya kart ile ödeyin.
        </span>
      </div>

      <Button
        onClick={onGoToCoupons}
        variant="outline"
        size="sm"
        data-testid={`cust-exit-coupon-btn-${s.plate}`}
        className="w-full rounded-sm uppercase tracking-widest text-[11px] font-semibold"
      >
        <TicketIcon size={12} /> Kupon Kodum Var
      </Button>
    </div>
  );
}

function PhotoTile({ src, label }) {
  const [failed, setFailed] = React.useState(false);
  const showImg = src && !failed;
  return (
    <div className="relative w-14 h-14 rounded-sm overflow-hidden bg-muted border border-border shrink-0">
      {showImg ? (
        <img src={src} alt={label} className="w-full h-full object-cover"
             onError={() => setFailed(true)} />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-0.5">
          <ImageSquare size={20} />
          <span className="text-[7px] uppercase tracking-widest opacity-60">Yok</span>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[8px] uppercase tracking-widest text-center">
        {label}
      </div>
    </div>
  );
}
